import React, { useEffect, useState, useRef, Component } from 'react';
import { XIcon, SendIcon, MessageSquareIcon, FileTextIcon, ListIcon, SearchIcon } from 'lucide-react';
interface AlgaLessonDialogProps {
  isOpen: boolean;
  onClose: () => void;
  module: number;
  lesson: number;
}
type InteractionOption = 'chat' | 'summary' | 'outline' | 'deep-dive';
const AlgaLessonDialog: React.FC<AlgaLessonDialogProps> = ({
  isOpen,
  onClose,
  module,
  lesson
}) => {
  const [selectedOption, setSelectedOption] = useState<InteractionOption | null>(null);
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  // Close dialog when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);
  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setSelectedOption(null);
        setQuery('');
        setResponse(null);
      }, 300);
    }
  }, [isOpen]);
  const handleOptionSelect = (option: InteractionOption) => {
    setSelectedOption(option);
    setResponse(null);
    // Auto-generate content based on the option
    if (option === 'summary') {
      setIsLoading(true);
      setTimeout(() => {
        setResponse('## Riassunto della lezione: Introduzione alle strategie ESG\n\n' + 'Le strategie ESG rappresentano un approccio integrato alla sostenibilità aziendale che considera tre dimensioni chiave:\n\n' + "1. **Ambientale (E)**: Gestione dell'impatto ambientale, efficienza delle risorse, emissioni di carbonio, e biodiversità.\n\n" + '2. **Sociale (S)**: Relazioni con dipendenti, fornitori, clienti e comunità, inclusi aspetti come diversità, diritti umani e sicurezza sul lavoro.\n\n' + '3. **Governance (G)**: Strutture di leadership, compensi esecutivi, audit, controlli interni e diritti degli azionisti.\n\n' + "L'implementazione efficace delle strategie ESG richiede un approccio sistematico che includa la valutazione dei rischi e delle opportunità, la definizione di KPI misurabili, e l'integrazione dei fattori ESG nel processo decisionale aziendale.");
        setIsLoading(false);
      }, 1500);
    } else if (option === 'outline') {
      setIsLoading(true);
      setTimeout(() => {
        setResponse('## Schema della lezione: Introduzione alle strategie ESG\n\n' + '1. **Fondamenti delle strategie ESG**\n' + '   - Definizione e evoluzione del concetto ESG\n' + '   - Importanza nel contesto attuale\n' + '   - Differenza tra ESG e CSR\n\n' + '2. **Componenti delle strategie ESG**\n' + '   - Fattori ambientali\n' + '   - Fattori sociali\n' + '   - Fattori di governance\n\n' + '3. **Framework e standard ESG**\n' + '   - GRI (Global Reporting Initiative)\n' + '   - SASB (Sustainability Accounting Standards Board)\n' + '   - TCFD (Task Force on Climate-related Financial Disclosures)\n\n' + '4. **Implementazione delle strategie ESG**\n' + '   - Valutazione della materialità\n' + '   - Definizione degli obiettivi\n' + '   - Misurazione e reporting\n\n' + '5. **Case studies**\n' + '   - Esempi di successo\n' + '   - Lezioni apprese\n' + '   - Best practices');
        setIsLoading(false);
      }, 1500);
    } else if (option === 'deep-dive') {
      setIsLoading(true);
      setTimeout(() => {
        setResponse('## Approfondimento: Framework ESG e loro applicazione\n\n' + '### I principali framework ESG\n\n' + '**GRI (Global Reporting Initiative)**\n' + 'Il GRI fornisce standard globali per il reporting di sostenibilità, offrendo linee guida dettagliate per la divulgazione degli impatti economici, ambientali e sociali. È il framework più ampiamente utilizzato a livello globale.\n\n' + '**SASB (Sustainability Accounting Standards Board)**\n' + 'Il SASB ha sviluppato standard specifici per settore che identificano i problemi di sostenibilità finanziariamente rilevanti. Questi standard aiutano le aziende a divulgare informazioni di sostenibilità utili per gli investitori.\n\n' + '**TCFD (Task Force on Climate-related Financial Disclosures)**\n' + 'Il TCFD si concentra specificamente sulla divulgazione dei rischi e delle opportunità legati al clima. Fornisce raccomandazioni su come le aziende dovrebbero valutare e riportare i rischi finanziari legati al clima.\n\n' + '### Come scegliere il framework più adatto\n\n' + 'La scelta del framework ESG dipende da diversi fattori:\n\n' + '1. **Obiettivi di reporting**: GRI per un approccio completo, SASB per informazioni rilevanti per gli investitori, TCFD per focus sul clima.\n\n' + '2. **Settore industriale**: Alcuni framework hanno linee guida specifiche per settore.\n\n' + '3. **Audience target**: Investitori, clienti, dipendenti o regolatori.\n\n' + '4. **Risorse disponibili**: Alcuni framework richiedono più dati e analisi di altri.\n\n' + 'Molte aziende stanno adottando un approccio ibrido, utilizzando elementi di diversi framework per creare un sistema di reporting ESG completo e rilevante per i loro stakeholder specifici.');
        setIsLoading(false);
      }, 1500);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setIsLoading(true);
    setTimeout(() => {
      setResponse('Grazie per la tua domanda sulle strategie ESG! I fattori ESG più critici per le piccole e medie imprese sono:\n\n' + '1. **Ambientali**: Efficienza energetica e gestione dei rifiuti, che possono portare a risparmi sui costi operativi.\n\n' + '2. **Sociali**: Pratiche di lavoro eque e sviluppo del capitale umano, che migliorano la produttività e riducono il turnover.\n\n' + '3. **Governance**: Etica aziendale e trasparenza, che costruiscono fiducia con clienti e partner.\n\n' + "Per le PMI, è consigliabile iniziare con un approccio graduale, concentrandosi su aree che si allineano con la strategia aziendale e possono generare valore a breve termine, come l'efficienza delle risorse o il miglioramento della soddisfazione dei dipendenti.");
      setIsLoading(false);
    }, 2000);
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-id="element-723">
      <div ref={dialogRef} className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden animate-fadeIn" data-id="element-724">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200" data-id="element-725">
          <div className="flex items-center" data-id="element-726">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-pink-500 mr-3" data-id="element-727">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/x28iigDaSByhSaf2N4iVm6/IMG_0007.png" alt="ALGA AI Assistant" className="w-full h-full object-cover" data-id="element-728" />
            </div>
            <div data-id="element-729">
              <h2 className="text-base font-semibold text-gray-800" data-id="element-730">
                Supporto alla lezione
              </h2>
              <p className="text-xs text-gray-500" data-id="element-731">
                Powered by EIIS Intelligence
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 text-gray-500" data-id="element-732">
            <XIcon size={20} data-id="element-733" />
          </button>
        </div>
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4" data-id="element-734">
          {!selectedOption ? <div className="space-y-4" data-id="element-735">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-736">
                Come posso aiutarti con questa lezione?
              </h3>
              <button onClick={() => handleOptionSelect('chat')} className="w-full flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-737">
                <div className="w-10 h-10 rounded-full bg-custom-green/10 flex items-center justify-center mr-3" data-id="element-738">
                  <MessageSquareIcon size={20} className="text-custom-green" data-id="element-739" />
                </div>
                <div className="text-left" data-id="element-740">
                  <h4 className="font-medium text-gray-800" data-id="element-741">Chatta con ALGA</h4>
                  <p className="text-sm text-gray-500" data-id="element-742">
                    Fammi domande specifiche sulla lezione
                  </p>
                </div>
              </button>
              <button onClick={() => handleOptionSelect('summary')} className="w-full flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-743">
                <div className="w-10 h-10 rounded-full bg-custom-green/10 flex items-center justify-center mr-3" data-id="element-744">
                  <FileTextIcon size={20} className="text-custom-green" data-id="element-745" />
                </div>
                <div className="text-left" data-id="element-746">
                  <h4 className="font-medium text-gray-800" data-id="element-747">
                    Riassunto della lezione
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-748">
                    Ottieni un riassunto dei concetti chiave
                  </p>
                </div>
              </button>
              <button onClick={() => handleOptionSelect('outline')} className="w-full flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-749">
                <div className="w-10 h-10 rounded-full bg-custom-green/10 flex items-center justify-center mr-3" data-id="element-750">
                  <ListIcon size={20} className="text-custom-green" data-id="element-751" />
                </div>
                <div className="text-left" data-id="element-752">
                  <h4 className="font-medium text-gray-800" data-id="element-753">
                    Schema della lezione
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-754">
                    Visualizza la struttura e i punti principali
                  </p>
                </div>
              </button>
              <button onClick={() => handleOptionSelect('deep-dive')} className="w-full flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-755">
                <div className="w-10 h-10 rounded-full bg-custom-green/10 flex items-center justify-center mr-3" data-id="element-756">
                  <SearchIcon size={20} className="text-custom-green" data-id="element-757" />
                </div>
                <div className="text-left" data-id="element-758">
                  <h4 className="font-medium text-gray-800" data-id="element-759">Approfondimento</h4>
                  <p className="text-sm text-gray-500" data-id="element-760">
                    Esplora concetti avanzati e materiali aggiuntivi
                  </p>
                </div>
              </button>
            </div> : <div className="space-y-4" data-id="element-761">
              {selectedOption === 'chat' && <div className="mb-4" data-id="element-762">
                  <h3 className="text-lg font-medium text-gray-800 mb-2" data-id="element-763">
                    Chatta con ALGA
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-764">
                    Fammi domande specifiche sulla lezione "Introduzione alle
                    strategie ESG"
                  </p>
                </div>}
              {response && <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-line" data-id="element-765">
                  <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{
              __html: response.replace(/\n\n/g, '<br/><br/>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            }} data-id="element-766" />
                </div>}
              {isLoading && <div className="flex justify-center items-center py-8" data-id="element-767">
                  <div className="flex space-x-2 items-center" data-id="element-768">
                    <div className="w-3 h-3 bg-custom-green rounded-full animate-bounce" data-id="element-769"></div>
                    <div className="w-3 h-3 bg-custom-green rounded-full animate-bounce" style={{
                animationDelay: '0.2s'
              }} data-id="element-770"></div>
                    <div className="w-3 h-3 bg-custom-green rounded-full animate-bounce" style={{
                animationDelay: '0.4s'
              }} data-id="element-771"></div>
                  </div>
                </div>}
              {selectedOption === 'chat' && <div className="mt-4" data-id="element-772">
                  <form onSubmit={handleSubmit} className="flex items-center" data-id="element-773">
                    <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Scrivi la tua domanda..." className="flex-1 p-3 rounded-l-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-custom-green focus:border-transparent" disabled={isLoading} data-id="element-774" />
                    <button type="submit" className="bg-custom-green text-white p-3 rounded-r-lg hover:bg-custom-green/90 focus:outline-none focus:ring-2 focus:ring-custom-green disabled:opacity-50" disabled={!query.trim() || isLoading} data-id="element-775">
                      <SendIcon size={20} data-id="element-776" />
                    </button>
                  </form>
                </div>}
            </div>}
        </div>
        {/* Footer */}
        {selectedOption && <div className="p-4 border-t border-gray-200" data-id="element-777">
            <button onClick={() => setSelectedOption(null)} className="text-sm text-gray-500 hover:text-gray-700" data-id="element-778">
              ← Torna alle opzioni
            </button>
          </div>}
      </div>
    </div>;
};
export default AlgaLessonDialog;